import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ProductCard } from "./ProductCard";

const API_URL = "https://admin.bodiosyakdown.com/api";

const SORT_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
];

const COLOR_CLASS_MAP = {
  Black: "bg-black", White: "bg-white border border-gray-300",
  "White yak": "bg-gray-100 border border-gray-200", "Brown yak": "bg-yellow-800",
  "Silver yak": "bg-gray-400", Beige: "bg-[#F5F5DC] border border-gray-200",
  Blue: "bg-blue-500", Red: "bg-red-500", Green: "bg-green-500",
  Gray: "bg-gray-400", Brown: "bg-yellow-800", "Dark brown": "bg-[#654321]",
  "Dark brown mixed": "bg-[#5C4033]", "Light brown": "bg-[#A67B5B]",
  "Light gray": "bg-gray-200 border border-gray-300", "Light grey": "bg-gray-200 border border-gray-300",
  "Cloudy grey": "bg-[#B8B8B8]", "Cloudy grey mixed": "bg-[#9E9E9E]",
  "black mixed": "bg-[#36454F]", "Navy Blue": "bg-blue-900",
  "Olive Green": "bg-green-700",
};

function safeParseJson(val, fallback = []) {
  if (Array.isArray(val)) return val;
  if (!val) return fallback;
  try { return JSON.parse(val); } catch { return fallback; }
}

export function CollectionPage() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("default");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const sidebarRef = useRef(null);

  useEffect(() => {
    fetchProductsByCategory(category);
    setSelectedColors([]);
    setSelectedSizes([]);
    setSortBy("default");
  }, [category]);

  useEffect(() => {
    if (filterOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [filterOpen]);

  const fetchProductsByCategory = async (cat) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/products`);
      const all = Array.isArray(res.data) ? res.data : [];
      const filtered = all
        .filter((p) => p.category && p.category.toLowerCase() === cat.toLowerCase())
        .map((p) => ({
          ...p,
          _colors: safeParseJson(p.color),
          _sizes: safeParseJson(p.size),
          _channelPrice: p.channelPrice || safeParseJson(p.channel_price),
        }));
      setProducts(filtered);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const allColors = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      p._colors?.forEach((c) => { if (c?.trim()) set.add(c.trim()); });
      p._channelPrice?.forEach((cp) => { if (cp?.color?.trim()) set.add(cp.color.trim()); });
    });
    return [...set].sort();
  }, [products]);

  const allSizes = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      p._sizes?.forEach((s) => { if (s?.trim()) set.add(s.trim()); });
      p._channelPrice?.forEach((cp) => { if (cp?.size?.trim()) set.add(cp.size.trim()); });
    });
    return [...set].sort();
  }, [products]);

  const filteredAndSorted = useMemo(() => {
    let list = [...products];

    if (selectedColors.length > 0) {
      list = list.filter((p) => {
        const directColors = (p._colors || []).map((c) => c?.trim());
        const cpColors = (p._channelPrice || []).map((cp) => cp?.color?.trim()).filter(Boolean);
        const pColors = [...new Set([...directColors, ...cpColors])];
        return selectedColors.some((sc) => pColors.includes(sc));
      });
    }

    if (selectedSizes.length > 0) {
      list = list.filter((p) => {
        const directSizes = (p._sizes || []).map((s) => s?.trim());
        const cpSizes = (p._channelPrice || []).map((cp) => cp?.size?.trim()).filter(Boolean);
        const pSizes = [...new Set([...directSizes, ...cpSizes])];
        return selectedSizes.some((ss) => pSizes.includes(ss));
      });
    }

    if (sortBy === "price_asc") {
      list.sort((a, b) => Number(a.min_price || a.price || 0) - Number(b.min_price || b.price || 0));
    } else if (sortBy === "price_desc") {
      list.sort((a, b) => Number(b.min_price || b.price || 0) - Number(a.min_price || a.price || 0));
    }

    return list;
  }, [products, selectedColors, selectedSizes, sortBy]);

  const toggleColor = (c) =>
    setSelectedColors((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const toggleSize = (s) =>
    setSelectedSizes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const clearAll = () => { setSelectedColors([]); setSelectedSizes([]); setSortBy("default"); };

  const activeFilterCount = selectedColors.length + selectedSizes.length + (sortBy !== "default" ? 1 : 0);

  const categoryName = category ? category.charAt(0).toUpperCase() + category.slice(1) : "";
  const breadcrumbCategoryLabel = categoryName;

  const brandDescription =
    "BODIOS, a distinguished family-owned Mongolian brand with a heritage spanning 30 years, is now proudly led by its second generation. With privileged access to 60% of Mongolia's yak down, we meticulously oversee each step of production to achieve extraordinary softness—a true embodiment of luxury.";

  const filterFont = { fontFamily: "'Githera Regular', sans-serif", fontWeight: 400 };

  return (
    <div className="flex flex-col text-black pt-[120px] max-md:pt-[80px] max-w-[1920px] mx-auto lg:mt-10">
      {/* Breadcrumb */}
      <div className="w-full px-8 md:px-12 lg:px-[83px] mb-5">
        <nav className="flex items-center gap-2 text-[13px] text-[#6f675e]">
          <Link to="/" className="hover:text-black transition-colors">Home</Link>
          <span>›</span>
          <Link to="/category/women" className="hover:text-black transition-colors">All products</Link>
          <span>›</span>
          <span className="text-[#2c2520]">{breadcrumbCategoryLabel}</span>
        </nav>
      </div>

      {/* Category Title */}
      <h1
        className="self-center text-center uppercase max-md:text-4xl"
        style={{ fontFamily: "'Githera Regular', sans-serif", fontSize: "clamp(36px, 5vw, 60px)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "0.02em" }}
      >
        {categoryName}
      </h1>

      {/* Description */}
      <p
        className="self-center mt-8 text-center max-w-[1000px] px-6"
        style={{ fontFamily: "Roboto, sans-serif", fontWeight: 300, fontSize: "clamp(11px, 0.75vw, 18px)", lineHeight: "22px", color: "#2c2520" }}
      >
        {brandDescription}
      </p>

      {/* Filter Bar */}
      <div className="flex justify-between items-center w-full mt-10 px-8 md:px-12 lg:px-[83px] py-4">
        <button
          onClick={() => setFilterOpen(true)}
          className="text-[13px] uppercase tracking-[0.1em] text-black hover:opacity-60 transition-opacity flex items-center gap-2"
          style={filterFont}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          FILTER
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-black text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <span className="text-[13px] uppercase tracking-[0.1em]" style={filterFont}>
          {filteredAndSorted.length} PRODUCTS
        </span>
      </div>

      {/* Filter Sidebar Overlay */}
      {filterOpen && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setFilterOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />

          {/* Sidebar */}
          <div
            ref={sidebarRef}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-[320px] max-w-[85vw] h-full bg-white shadow-2xl overflow-y-auto animate-slideInLeft"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b">
              <h2 className="text-[15px] uppercase tracking-[0.15em] font-medium">Filter</h2>
              <button onClick={() => setFilterOpen(false)} className="p-1 hover:opacity-60">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Sort */}
            <div className="px-6 py-5 border-b">
              <h3 className="text-[13px] uppercase tracking-[0.12em] font-medium mb-4">Sort by</h3>
              <div className="flex flex-col gap-2">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`text-left text-[13px] py-1.5 transition-colors ${
                      sortBy === opt.value ? "text-black font-medium" : "text-gray-500 hover:text-black"
                    }`}
                    style={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    {sortBy === opt.value ? "• " : "  "}{opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="px-6 py-5 border-b">
              <h3 className="text-[13px] uppercase tracking-[0.12em] font-medium mb-4">Colour</h3>
              {allColors.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-2.5">
                    {allColors.map((c) => (
                      <button
                        key={c}
                        onClick={() => toggleColor(c)}
                        title={c}
                        className={`w-8 h-8 rounded-sm transition-all ${
                          COLOR_CLASS_MAP[c] || "bg-gray-300"
                        } ${selectedColors.includes(c) ? "ring-2 ring-offset-2 ring-black" : "hover:ring-1 hover:ring-gray-400"}`}
                      />
                    ))}
                  </div>
                  {selectedColors.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {selectedColors.map((c) => (
                        <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-gray-100 rounded">
                          {c}
                          <button onClick={() => toggleColor(c)} className="hover:text-red-500">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[12px] text-gray-400">No colours available</p>
              )}
            </div>

            {/* Sizes */}
            <div className="px-6 py-5 border-b">
              <h3 className="text-[13px] uppercase tracking-[0.12em] font-medium mb-4">Size</h3>
              {allSizes.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {allSizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleSize(s)}
                      className={`py-2 text-[12px] text-center border transition-all ${
                        selectedSizes.includes(s)
                          ? "border-black bg-black text-white"
                          : "border-gray-200 text-gray-700 hover:border-black"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-gray-400">No sizes available</p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-5 flex gap-3">
              <button
                onClick={clearAll}
                className="flex-1 py-3 text-[13px] uppercase tracking-wider border border-gray-300 hover:border-black transition-colors"
              >
                Clear all
              </button>
              <button
                onClick={() => setFilterOpen(false)}
                className="flex-1 py-3 text-[13px] uppercase tracking-wider bg-black text-white hover:bg-gray-800 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Grid */}
      {loading ? (
        <div className="flex justify-center mt-20">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredAndSorted.length === 0 ? (
        <p className="text-center mt-20 text-xl text-gray-400">
          No products found.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-10 px-8 md:px-12 lg:px-16 mt-2">
          {filteredAndSorted.map((product) => (
            (() => {
              const productColors = [
                ...(product._channelPrice || []).map((cp) => cp?.color?.trim()).filter(Boolean),
                ...(product._colors || []).map((c) => c?.trim()).filter(Boolean),
              ];
              const uniqueColors = [...new Set(productColors)];
              const colorSwatches = uniqueColors.map((colorName) => ({
                name: colorName,
                className: COLOR_CLASS_MAP[colorName] || "bg-gray-300",
              }));

              return (
            <ProductCard
              key={product.id}
              id={product.id}
              name={product.name}
              image={product.mainImage}
              price={product.min_price || product.price}
              stock={product.stock}
              colors={colorSwatches}
            />
              );
            })()
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
